/** Navigation-timing sampling: pure helpers shared by the client sampler
 *  (`components/perf/navigation-timing.tsx`) and its tests.
 *
 *  WHY this exists: PostHog's `$web_vitals` only fires on HARD page loads.
 *  App Router navigations are soft (an RSC fetch plus a client render), so
 *  the metric users actually complain about, "clicking a link feels slow",
 *  was completely uninstrumented. LCP looked merely "slightly worse" over
 *  the same window in which navigation felt materially worse, because LCP
 *  was measuring a different thing.
 *
 *  The sink is PostHog rather than Vercel Speed Insights deliberately:
 *  Speed Insights has no query API, so its numbers can only be read by a
 *  human in a dashboard. Landing this in PostHog means the same HogQL that
 *  drives every other investigation here can rank routes by p75.
 *
 *  Prefetch handling is decided by the CALLER keying off `usePathname`, not
 *  here: see the header comment on `applyServerTiming` in `apps/web/proxy.ts`
 *  for the measured reason a server-side prefetch signal is unavailable.
 */

/** PostHog event name. Lives here rather than in the component so a grep
 *  across `lib/` finds the name next to the payload builder, matching the
 *  `lib/analytics/{nps,rubric-feedback,scenario-feedback}.ts` client
 *  convention. Query with HogQL:
 *
 *    SELECT properties.route,
 *           quantile(0.75)(toFloat(properties.duration_ms)) AS p75_ms,
 *           quantile(0.75)(toFloat(properties.gate_ms))     AS p75_gate,
 *           count() AS n
 *    FROM events
 *    WHERE event = 'navigation_timing'
 *      AND timestamp >= now() - INTERVAL 7 DAY
 *      AND properties.kind = 'rsc'
 *    GROUP BY 1 ORDER BY p75_ms DESC
 */
export const NAVIGATION_TIMING_EVENT = "navigation_timing";

/** `Server-Timing` metric names. `proxy.ts` writes these and this module
 *  reads them, so both sides import from here — a literal on either side
 *  could be renamed independently and silently null out the metric. */
export const SERVER_TIMING = {
  /** Whole-proxy wall time, including both reads below. */
  total: "mw",
  /** `resolveClaims`: JWT verify plus any transparent token refresh. */
  claims: "claims",
  /** The `profiles` auth-gate read. */
  gate: "gate",
} as const;

/** Only `/auth/*` is reachable here. Both call sites pass `usePathname()`,
 *  which only ever yields an App Router PAGE path, so `/api/*`, `/_next/*`
 *  and the `/medceptor-relay` rewrite can never appear. Listing them would
 *  read as load-bearing while guarding nothing. */
const IGNORED_PREFIXES = ["/auth/"];

export function isIgnoredPath(pathname: string): boolean {
  return IGNORED_PREFIXES.some((p) => pathname.startsWith(p));
}

/** Rebuilds the Next route PATTERN (e.g. `/questions/exam/[sessionId]`) from
 *  a concrete pathname plus `useParams()`, so PostHog groups by route rather
 *  than by URL. Grouping on raw paths would blow cardinality up (709
 *  scenarios, ~13.3k questions, one bucket each) and make a p75-per-route
 *  query meaningless.
 *
 *  Derived rather than table-driven on purpose. An earlier revision carried a
 *  hand-maintained map of dynamic families plus a `staticChildren` allowlist,
 *  and it was already wrong on the day it was written: it collapsed the real
 *  page `/questions/exam` into `/questions/[slug]` and gave every exam session
 *  its own bucket. Reading the router's own params cannot drift when a route
 *  is added.
 *
 *  Known limitation: a static segment whose literal text equals a param value
 *  elsewhere in the same path is substituted too. Harmless for these routes
 *  (slugs and UUIDs do not collide with `exam` / `practice` / `progress`). */
export function routePattern(
  pathname: string,
  // Matches Next's `Params`, whose values are optional.
  params: Readonly<Record<string, string | string[] | undefined>>,
): string {
  const tokenByValue = new Map<string, string>();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined) continue;
    if (Array.isArray(value)) {
      // Catch-all: every element is one segment of the same token.
      for (const part of value) tokenByValue.set(part, `[...${key}]`);
    } else {
      tokenByValue.set(value, `[${key}]`);
    }
  }

  const out: string[] = [];
  for (const raw of pathname.split("/").filter(Boolean)) {
    // `pathname` is URL-encoded; `params` values are decoded.
    let decoded = raw;
    try {
      decoded = decodeURIComponent(raw);
    } catch {
      // Malformed escape: fall back to the raw segment.
    }
    const token = tokenByValue.get(decoded);
    if (token === undefined) {
      out.push(raw);
      continue;
    }
    // A catch-all spans several segments but is one token in the pattern.
    if (token.startsWith("[...") && out.at(-1) === token) continue;
    out.push(token);
  }
  return out.length > 0 ? `/${out.join("/")}` : "/";
}

interface ServerTimingLike {
  readonly name: string;
  readonly duration: number;
}

/** Minimal structural view of `PerformanceResourceTiming` /
 *  `PerformanceNavigationTiming`, carrying only the fields read below. */
export interface RawTimingEntry {
  /** Absolute URL of the request. */
  readonly name: string;
  readonly duration: number;
  readonly requestStart: number;
  readonly responseStart: number;
  readonly responseEnd: number;
  readonly initiatorType?: string;
  readonly serverTiming?: readonly ServerTimingLike[];
}

/** How the route was delivered.
 *  - `doc`        hard page load (first paint of the tab)
 *  - `rsc`        soft navigation that issued an RSC fetch and waited on it
 *  - `prefetched` soft navigation served from the prefetch cache; no request
 *                 was on the critical path, so it is the FAST case and must
 *                 be recorded rather than dropped (dropping it would keep
 *                 only the slow half and bias p75 upward) */
type NavigationKind = "doc" | "rsc" | "prefetched";

interface NavigationSample {
  route: string;
  kind: NavigationKind;
  /** Full request duration in ms. 0 for `prefetched`. */
  duration_ms: number;
  /** Time to first byte in ms, the server-side share of `duration_ms`. */
  ttfb_ms: number;
  /** Whole-proxy wall time. Null when the browser omits `serverTiming`
   *  (notably older Safari) or on a public route where the proxy returns
   *  before the gate runs. */
  mw_ms: number | null;
  claims_ms: number | null;
  gate_ms: number | null;
}

/** Rounded to whole ms. Sub-ms precision is noise at this scale and PostHog
 *  property values compress better as integers. */
function ms(value: number): number {
  return Math.round(value);
}

function metricFrom(entry: RawTimingEntry, name: string): number | null {
  const found = entry.serverTiming?.find((t) => t.name === name);
  return found ? ms(found.duration) : null;
}

/** The pathname an entry served, if it is a same-origin RSC navigation
 *  fetch. Null for anything else (images, scripts, API calls, beacons). */
export function rscPathOf(
  entry: RawTimingEntry,
  origin: string,
): string | null {
  if (entry.initiatorType !== "fetch") return null;
  // `duration` is 0 for cached or aborted entries; nothing useful to report.
  if (!(entry.duration > 0)) return null;
  let url: URL;
  try {
    url = new URL(entry.name);
  } catch {
    return null;
  }
  if (url.origin !== origin) return null;
  // Next tags every RSC fetch with a `_rsc` cache-busting search param.
  if (!url.searchParams.has("_rsc")) return null;
  return url.pathname;
}

/** Picks the RSC fetch that served `pathname`, or null when the navigation
 *  was served from the prefetch cache.
 *
 *  `since` scopes the search to entries that finished after the previous
 *  navigation was recorded, so a stale prefetch of the same route from
 *  minutes ago is never mistaken for this navigation's fetch. */
export function selectRscEntry(
  entries: readonly RawTimingEntry[],
  pathname: string,
  origin: string,
  since: number,
): RawTimingEntry | null {
  let best: RawTimingEntry | null = null;
  for (const entry of entries) {
    if (entry.responseEnd < since) continue;
    if (rscPathOf(entry, origin) !== pathname) continue;
    if (!best || entry.responseEnd > best.responseEnd) best = entry;
  }
  return best;
}

/** Builds the sample for a completed navigation.
 *  `entry` is the RSC fetch (or document entry) that served it, or null when
 *  it came from the prefetch cache. */
export function buildNavigationSample(args: {
  route: string;
  hardLoad: boolean;
  entry: RawTimingEntry | null;
}): NavigationSample {
  const { route, hardLoad, entry } = args;

  if (!entry) {
    // Prefetch-cache hit: nothing was on the critical path.
    return {
      route,
      kind: "prefetched",
      duration_ms: 0,
      ttfb_ms: 0,
      mw_ms: null,
      claims_ms: null,
      gate_ms: null,
    };
  }

  return {
    route,
    kind: hardLoad ? "doc" : "rsc",
    duration_ms: ms(entry.duration),
    ttfb_ms: ms(Math.max(0, entry.responseStart - entry.requestStart)),
    mw_ms: metricFrom(entry, SERVER_TIMING.total),
    claims_ms: metricFrom(entry, SERVER_TIMING.claims),
    gate_ms: metricFrom(entry, SERVER_TIMING.gate),
  };
}
